import { Cpu } from 'lucide-react'
import { useEffect } from 'react'

import { useIsAdmin, useMe } from '@/api/endpoints/me'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
// Same de-facto shared form stylesheet every sibling advanced panel uses
// (``advSection``, ``advSectionLabel``, ``rowIcon``). See NarratorPicker for
// the note on why it still lives under ViralShortsForm.
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'

export type LlmProvider = 'auto' | 'cloud' | 'local'

const AUTO_ADMIN: SegmentedOption<LlmProvider> = {
  value: 'auto',
  label: 'Padrão',
  title: 'Segue o padrão global por área (algumas factories já usam o modelo local).',
}

const AUTO: SegmentedOption<LlmProvider> = {
  value: 'auto',
  label: 'Padrão',
  title: 'Segue o padrão global por área.',
}

const CLOUD: SegmentedOption<LlmProvider> = {
  value: 'cloud',
  label: 'Nuvem',
  title: 'Modelo de nuvem — melhor qualidade, consome tokens pagos.',
}

const LOCAL: SegmentedOption<LlmProvider> = {
  value: 'local',
  label: 'Local',
  title: 'Modelo local (Ollama) — grátis, roda na sua máquina; mais lento.',
}

/**
 * As opções que a conta pode escolher. O modelo local é infraestrutura do
 * operador (docs/plan-llm-local-admin.md): para quem não é admin, ``local``
 * some, e o backend responde 403 se ele vier mesmo assim.
 */
function llmProviderOptions(isAdmin: boolean): ReadonlyArray<SegmentedOption<LlmProvider>> {
  return isAdmin ? [AUTO_ADMIN, CLOUD, LOCAL] : [AUTO, CLOUD]
}

export interface LlmProviderPickerProps {
  value: LlmProvider
  onChange: (value: LlmProvider) => void
}

/**
 * Shared LLM-provider picker for every factory form's "advanced" panel.
 *
 * Chooses where the roteiro + metadata (SEO) of THIS run generate: follow the
 * global default (``Padrão``), force the cloud provider (``Nuvem`` —
 * the configured cloud model), or the local Ollama model (``Local``, admin
 * only). Ranking, safety, and vision always stay on the cloud provider
 * regardless of this choice.
 *
 * A ``local`` already loaded (a preset saved before the rule) shows as
 * ``Padrão`` for a non-admin, and the form gets ``auto`` back, so the start
 * does not hit the 403. Only once ``/me`` answered: while it loads the admin
 * would lose their own ``local``.
 *
 * Mirrors the ``NarratorPicker`` contract: the form just wires a value + setter;
 * the widget owns the section chrome so no form has to duplicate it.
 */
export function LlmProviderPicker({ value, onChange }: LlmProviderPickerProps) {
  const isAdmin = useIsAdmin()
  const known = useMe().isSuccess
  const refused = known && !isAdmin && value === 'local'

  useEffect(() => {
    if (refused) onChange('auto')
  }, [refused, onChange])

  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          <Cpu />
        </span>
        Modelo de IA
      </div>
      <Segmented<LlmProvider>
        options={llmProviderOptions(isAdmin)}
        value={!isAdmin && value === 'local' ? 'auto' : value}
        onValueChange={onChange}
        aria-label="Modelo de IA (roteiro + metadados)"
      />
    </section>
  )
}
